/**
 * The Sunday-start week, and the two ways a day inside it can be named.
 *
 * Written for vitamins and now used by blood work, weight and groceries as well, which is
 * why it lives under `calendar` rather than under the first feature that needed it. A
 * screen asking "which days of this week" should not have to import another feature to
 * find out.
 *
 * Two representations meet here and they are easy to confuse, so each is named:
 *
 *   a **weekday** is `0`-`6` with Sunday at `0`, exactly as `Date#getUTCDay` and the month
 *   grid's first column agree. This is what a vitamin remembers, because a remembered
 *   "Monday and Thursday" has to survive into next week.
 *
 *   a **date key** is one real calendar day. This is what adherence is stored against,
 *   because "taken" is a fact about a date, not about a weekday.
 *
 * Every function below converts in one direction only, so a caller never has to hold both
 * in its head. Note that this is a Sunday-start week to match the calendar the user
 * already reads; the training planner deliberately uses a Monday-start week with its own
 * index base, which is why these do not share constants with `domain/training/rest-plan`.
 */

import { addDateKeyDays, anchorInstantForDateKey } from '../../time/date-key.js';

/** Days in a week. Named so a bare 7 never has to be read for meaning. */
export const WEEK_LENGTH = 7;

/** Highest valid weekday index. */
export const MAX_WEEKDAY = WEEK_LENGTH - 1;

/**
 * Which weekday a date falls on. `0` is Sunday.
 *
 * @param {string} dateKey
 * @returns {number}
 */
export function weekdayOfDateKey(dateKey) {
  return anchorInstantForDateKey(dateKey).getUTCDay();
}

/**
 * The seven date keys of the Sunday-start week containing a date, Sunday first.
 *
 * @param {string} dateKey
 * @returns {string[]}
 */
export function weekDateKeys(dateKey) {
  const start = addDateKeyDays(dateKey, -weekdayOfDateKey(dateKey));
  return Array.from({ length: WEEK_LENGTH }, (_, offset) => addDateKeyDays(start, offset));
}

/**
 * Weekday indexes, deduplicated, ordered, and with anything out of range dropped.
 *
 * Applied to stored values as well as user input: a remembered list read back from the
 * database has been through however many versions of this feature, and a chip row that
 * renders `undefined` as a day is worse than one that quietly omits it.
 *
 * @param {unknown} days
 * @returns {number[]}
 */
export function normalizeWeekdays(days) {
  if (!Array.isArray(days)) return [];
  const valid = days.filter((day) => Number.isInteger(day) && day >= 0 && day <= MAX_WEEKDAY);
  return [...new Set(valid)].sort((left, right) => left - right);
}

/**
 * Remembered weekdays, as the dates they land on in one particular week.
 *
 * @param {string} dateKey  Any date in the week being planned.
 * @param {number[]} weekdays
 * @returns {string[]} Ordered Sunday first.
 */
export function weekdaysToDateKeys(dateKey, weekdays) {
  const keys = weekDateKeys(dateKey);
  return normalizeWeekdays(weekdays).map((weekday) => keys[weekday]);
}

/**
 * The weekdays a set of dates falls on.
 *
 * @param {string[]} dateKeys
 * @returns {number[]} Ordered Sunday first, deduplicated.
 */
export function dateKeysToWeekdays(dateKeys) {
  return normalizeWeekdays(dateKeys.map(weekdayOfDateKey));
}

/**
 * Whether two weekday lists mean the same thing.
 *
 * Used to decide whether a repeat choice is worth writing back to the vitamin, so that
 * logging a vitamin on the days it already remembers is one request rather than two.
 *
 * @param {number[]} left
 * @param {number[]} right
 * @returns {boolean}
 */
export function sameWeekdays(left, right) {
  const a = normalizeWeekdays(left);
  const b = normalizeWeekdays(right);
  return a.length === b.length && a.every((day, index) => day === b[index]);
}
